<template>
  <div :class="{ 'k-modal-show': show, 'k-error-modal': error }">
    <div class="k-modal" :id="modalId">
      <div class="k-modal-content" :class="{ 'pb-[4.5rem]': footer }">
        <div class="header">
          <CloseButton v-if="closeButton" @click="onCancel" data-testid="modal-close-button" />
          <slot v-if="show" name="header"></slot>
        </div>

        <div
          data-testid="modal-content-body"
          class="k-modal-content-body"
          :class="{ 'overflow-y-auto': scrollBody }"
          :style="`background-color: ${contentBodyColor}`"
        >
          <slot name="body">
            <p>
              Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor
              incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud
              exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
            </p>
          </slot>
        </div>
        <!-- k-modal-content-body -->
        <transition name="fade">
          <footer v-if="footer" class="fixed-footer">
            <div
              class="form-error-message"
              :class="{ show: errorMessage }"
              v-dompurify-html="errorMessage"
              data-testid="modal-error-message"
            ></div>

            <BusyButton
              ref="busy"
              data-testid="modal-submit-button"
              class="btn submit-button mr-4"
              v-if="submitButton"
              :busy="submitButtonBusy"
              :busyText="submitButtonBusyText"
              :class="{
                'mr-0': !cancelButton,
              }"
              :cls="`btn ${
                // TODO revise the BusyButton to not use cls prop
                submitButtonType === 'danger' ? 'btn-danger' : 'btn-primary'
              }`"
              @click="onSubmit"
            >
              <template #content>{{ submitButtonText }}</template>
            </BusyButton>

            <button
              data-testid="modal-cancel-button"
              class="btn btn-secondary btn-cancel"
              type="button"
              v-if="cancelButton"
              @click="onCancel"
            >
              {{ cancelButtonText }}
            </button>
          </footer>
        </transition>
      </div>
      <!-- k-modal-content -->
    </div>
    <!-- k-modal -->
    <div v-if="backdrop" class="k-modal-overlay"></div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';

import BusyButton from '@/components/BusyButton.vue';
import CloseButton from '@/components/CloseButton.vue';
import * as log from '@/logger';
import { useMainStore } from '@/stores/main';

export default {
  name: 'KModal',
  components: { BusyButton, CloseButton },
  emits: ['submit', 'close'],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    submitButtonText: {
      type: String,
      default: 'Ok',
    },
    cancelButtonText: {
      type: String,
      default: 'Cancel',
    },
    submitButtonType: {
      type: String,
      default: 'primary',
    },
    submitButtonBusy: {
      type: Boolean,
      default: false,
    },
    submitButtonBusyText: {
      type: String,
      default: '',
    },
    cancelButton: {
      type: Boolean,
      default: false,
    },
    closeButton: {
      type: Boolean,
      default: true,
    },
    footer: {
      type: Boolean,
      default: true,
    },
    submitButton: {
      type: Boolean,
      default: true,
    },
    backdrop: {
      type: Boolean,
      default: true,
    },
    contentBodyColor: {
      type: String,
      default: 'inherit',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    keyShortcutsEnabled: {
      type: Boolean,
      default: true,
    },
    modalId: {
      type: String,
      default: 'modal',
    },
    // if this modal was triggered from a Left Sidepanel, don't remove the preventSidepanelClose on modal close
    disallowSidePanelClose: {
      type: Boolean,
      default: false,
    },
    scrollBody: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      error: false,
    };
  },
  computed: {
    ...mapStores(useMainStore),
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.mainStore.preventSidepanelClose = true;
        setTimeout(() => {
          this.$refs.busy?.$refs.button.focus();
        }, 100);
      } else if (!this.disallowSidePanelClose) {
        setTimeout(() => (this.mainStore.preventSidepanelClose = false), 100);
      }
    },
  },
  methods: {
    onCancel() {
      log.info('Canceled modal');
      this.$emit('close');
    },
    onSubmit() {
      this.$emit('submit');
    },
    onKeydown(e) {
      if (!this.keyShortcutsEnabled) return;
      if (this.show && e.code === 'Escape') {
        // Don't close the modal if escape is meant to close an open combobox/popover inside this modal
        const active = document.activeElement;
        if (this.$el?.contains(active) && active?.getAttribute('aria-expanded') === 'true') return;
        this.onCancel();
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  unmounted() {
    document.removeEventListener('keydown', this.onKeydown);
  },
};
</script>

<style lang="scss" scoped>
.k-modal {
  box-shadow: $box-shadow;
  position: fixed;
  top: 50%;
  left: 50%;
  width: 100%;
  max-width: 40rem;
  min-width: 15rem;
  height: auto;
  z-index: $zindex-modal;
  visibility: hidden;
  transform: translateX(-50%) translateY(-50%);

  footer {
    :deep(.form-error-message) {
      .code {
        color: white !important;
        background-color: $color-darkened-150;
        padding: 2px 8px;
        border-radius: 3px;
      }
    }
  }

  .k-modal-content {
    color: $black;
    background: white;
    position: relative;
    border-radius: 0;
    margin: 0 auto;

    .k-error-modal > & {
      border-bottom-color: $red;
    }

    .header {
      margin: 0;
      padding: 1.1rem 2.2rem 1.1rem 2.5rem;
      text-align: left;
      opacity: 0.8;
      background: $gray-50;
      border-radius: 3px 3px 0 0;
      border-bottom: 1px solid $color-darkened-150;
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        margin-bottom: 0;
      }
    }

    .close-button {
      position: absolute;
      right: 0.7rem;
      top: 1.3rem;
    }

    & > .k-modal-content-body {
      padding: 1rem 2.5rem 1.25rem;
      margin: 0;
      font-size: 1rem;
      max-height: 80vh;

      p {
        margin: 0;
        padding: 10px 0;
      }

      ul {
        margin: 0;
        padding: 0 0 30px 20px;

        li {
          padding: 5px 0;
        }
      }
    }
  } /* k-modal-content */

  footer {
    padding: 1rem;
    text-align: center;

    .form-error-message.show {
      :deep(code) {
        color: white !important;
      }
    }
  }
} /* k-modal */

.k-modal-overlay {
  position: fixed;
  width: 100%;
  height: 100%;
  visibility: hidden;
  top: 0;
  left: 0;
  z-index: $zindex-modal-overlay;
  background-color: rgba($color: black, $alpha: 0);
  transition:
    visibility 0s linear 0.3s,
    background-color 0.3s;
}

.k-modal-content {
  transform: scale3d(0.97, 0.97, 0.97); // TODO: change to 0.9 when you fix the flicker problem.
  opacity: 0;
  transition: all 0.3s;
}

.k-modal-show {
  .k-modal {
    visibility: visible;
  }
  .k-modal-overlay {
    background-color: rgba($color: black, $alpha: 0.4);
    visibility: visible;
    transition:
      visibility 0s linear 0s,
      background-color 0.3s;
  }
  .k-modal-content {
    transform: scale3d(1, 1, 1);
    opacity: 1;
  }
}

@media (max-width: $bp-sm-max) {
  .k-modal {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    width: auto;
    height: auto;
    transform: none;

    footer {
      .btn {
        margin-bottom: 1rem;
      }
    }
  }
  .k-modal-content {
    .header {
      padding-left: 1rem !important;
    }
    .k-modal-content-body {
      padding-left: 1rem !important;
      padding-right: 1rem !important;
    }
  }
}
</style>
